import { useState } from 'react';
import { Product, categoryConfig, ProductStatus } from '../../../mocks/products';
import { agencyInfo } from '../../../mocks/dashboard';
import { useNavigate } from 'react-router-dom';

const STATUS_STYLES: Record<ProductStatus, string> = {
  Active:    'bg-emerald-100 text-emerald-700',
  Inactive:  'bg-gray-100 text-gray-500',
  'Sold Out':'bg-rose-100 text-rose-600',
};

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
  onEdit: (p: Product) => void;
}

export default function ProductDetailModal({ product, onClose, onEdit }: ProductDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'pricing' | 'supplier'>('overview');
  const navigate = useNavigate();
  const brand = agencyInfo.brandColor;
  const cfg = categoryConfig[product.category];
  const margin = Math.round(((product.sellPrice - product.costPrice) / product.sellPrice) * 100);
  const profit = product.sellPrice - product.costPrice;

  const fmtPrice = (n: number) =>
    `${product.currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const tabs = [
    { id: 'overview',  label: 'Overview',  icon: 'ri-information-line' },
    { id: 'pricing',   label: 'Pricing',    icon: 'ri-money-dollar-circle-line' },
    { id: 'supplier',  label: 'Supplier',   icon: 'ri-store-2-line' },
  ] as const;

  const stars = Array.from({ length: 5 }, (_, i) => i < Math.round(product.rating));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="w-full max-w-xl bg-white h-full flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero Image */}
        <div className="relative h-48 w-full flex-shrink-0 overflow-hidden bg-gray-100">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-black/30 text-white backdrop-blur-sm cursor-pointer hover:bg-black/50"
          >
            <i className="ri-close-line text-lg" />
          </button>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-white/90" style={{ color: cfg.color }}>
                <i className={`${cfg.icon} text-xs`} />
                {product.category}
              </span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[product.status]}`}>
                {product.status}
              </span>
              {product.featured && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-amber-500 text-white font-medium">Featured</span>
              )}
            </div>
            <h2 className="text-white font-bold text-lg leading-tight">{product.name}</h2>
            <p className="text-white/80 text-sm flex items-center gap-1 mt-0.5">
              <i className="ri-map-pin-line text-xs" />
              {product.destination} · {product.duration}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-5 pt-1 flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? '' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === tab.id ? { color: brand, borderColor: brand } : {}}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {activeTab === 'overview' && (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    {stars.map((f, i) => <i key={i} className={`text-xs ${f ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-200'}`} />)}
                  </div>
                  <p className="font-bold text-gray-800">{product.rating}</p>
                  <p className="text-xs text-gray-400">Rating</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="font-bold text-gray-800 text-lg">{product.timesBooked}</p>
                  <p className="text-xs text-gray-400">Times Booked</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="font-bold text-gray-800 text-sm">{product.minPax}–{product.maxPax}</p>
                  <p className="text-xs text-gray-400">Pax Range</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-2">Description</p>
                <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
              </div>

              {/* Inclusions */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-2">Inclusions</p>
                <div className="space-y-1.5">
                  {product.inclusions.map((inc, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <div className="w-4 h-4 flex items-center justify-center rounded-full bg-emerald-100 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-emerald-600" />
                      </div>
                      {inc}
                    </div>
                  ))}
                </div>
              </div>

              {/* Exclusions */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-2">Exclusions</p>
                <div className="space-y-1.5">
                  {product.exclusions.map((exc, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-gray-500">
                      <div className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-xs text-gray-400" />
                      </div>
                      {exc}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-2">Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {product.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="border border-gray-100 rounded-xl p-4 text-sm">
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Availability</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-400">Available From</p>
                    <p className="font-semibold text-gray-700">{new Date(product.availableFrom).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Available To</p>
                    <p className="font-semibold text-gray-700">{new Date(product.availableTo).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'pricing' && (
            <>
              {/* Price Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-400 mb-1">Cost Price</p>
                  <p className="font-bold text-gray-800 text-lg">{fmtPrice(product.costPrice)}</p>
                </div>
                <div className="rounded-xl p-4 text-center" style={{ backgroundColor: `${brand}10` }}>
                  <p className="text-xs text-gray-400 mb-1">Sell Price</p>
                  <p className="font-bold text-lg" style={{ color: brand }}>{fmtPrice(product.sellPrice)}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-400 mb-1">Profit</p>
                  <p className="font-bold text-emerald-700 text-lg">{fmtPrice(profit)}</p>
                </div>
              </div>

              {/* Margin bar */}
              <div className="border border-gray-100 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">Profit Margin</p>
                  <span className={`text-sm font-bold px-3 py-1 rounded-full ${margin >= 25 ? 'bg-emerald-100 text-emerald-700' : margin >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-600'}`}>
                    {margin}%
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all ${margin >= 25 ? 'bg-emerald-500' : margin >= 15 ? 'bg-amber-500' : 'bg-rose-500'}`}
                    style={{ width: `${Math.min(100, margin * 2)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-400">
                  <span>0%</span>
                  <span>25% target</span>
                  <span>50%+</span>
                </div>
              </div>

              {/* Pax pricing estimate */}
              <div className="border border-gray-100 rounded-xl p-5">
                <p className="text-sm font-semibold text-gray-700 mb-4">Revenue Estimate by Pax</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-50">
                      <th className="text-left py-2">Pax</th>
                      <th className="text-right py-2">Revenue</th>
                      <th className="text-right py-2">Cost</th>
                      <th className="text-right py-2">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[1, 2, 4, 6].filter((p) => p >= product.minPax && p <= product.maxPax).map((pax) => (
                      <tr key={pax}>
                        <td className="py-2 font-medium text-gray-700">{pax} pax</td>
                        <td className="py-2 text-right text-gray-600">{fmtPrice(pax * product.sellPrice)}</td>
                        <td className="py-2 text-right text-gray-500">{fmtPrice(pax * product.costPrice)}</td>
                        <td className="py-2 text-right text-emerald-600 font-semibold">{fmtPrice(pax * profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Booking Stats</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Total Bookings</p>
                    <p className="font-bold text-gray-800 text-xl">{product.timesBooked}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total Revenue Generated</p>
                    <p className="font-bold text-gray-800 text-xl">{fmtPrice(product.timesBooked * product.sellPrice)}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'supplier' && (
            <>
              <div className="border border-gray-100 rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: cfg.bg }}>
                    <i className={`${cfg.icon} text-lg`} style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{product.supplierName}</p>
                    <p className="text-xs text-gray-400">{product.category} Supplier</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50">
                      <i className="ri-mail-line text-gray-400 text-sm" />
                    </div>
                    <a href={`mailto:${product.supplierContact}`} className="hover:underline cursor-pointer">{product.supplierContact}</a>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50">
                      <i className="ri-file-text-line text-gray-400 text-sm" />
                    </div>
                    <span className="font-mono text-xs">{product.supplierRef}</span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-100 rounded-xl p-5">
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Pricing Breakdown</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Supplier Net Rate</span>
                    <span className="font-medium text-gray-800">{fmtPrice(product.costPrice)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Agency Markup</span>
                    <span className="font-medium text-emerald-600">+{fmtPrice(profit)} ({margin}%)</span>
                  </div>
                  <div className="flex justify-between text-gray-900 font-bold pt-2 border-t border-gray-100">
                    <span>Client Sell Price</span>
                    <span style={{ color: brand }}>{fmtPrice(product.sellPrice)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Supplier Reference</p>
                <p className="font-mono text-sm font-bold text-amber-800">{product.supplierRef}</p>
                <p className="text-xs text-amber-600 mt-1">Use this reference when booking directly with supplier</p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => onEdit(product)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line" />
            Edit Product
          </button>
          <button
            onClick={() => navigate('/bookings/new')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-calendar-check-line" />
            Use in Booking
          </button>
        </div>
      </div>
    </div>
  );
}
